import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router";
import { parseUserAgent } from "react-device-detect";
import BotExercise from "@nualang/nualang-ui-components/dist/Exercises/Bot";
import { isCourseAIEnabled } from "@nualang/nualang-ui-components/dist/utils/aiControls";
import { sendStudentMisuse as studentMisuseAPI } from "@nualang/nualang-api-and-queries/APIs/Notifications";
import {
  courses as courseQuerys,
  bots as botQuerys,
  progress as progressQuerys,
  completions as completionQuerys,
  users as userQuerys,
  classrooms as classroomQuerys,
  assignments as assignmentQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import useServices from "../../../hooks/useServices";
import useAudioPlayer from "../../..//hooks/useAudioPlayer";
import config from "../../../config";
import i18n from "../../../i18n";
import { messageBot, getBot } from "@nualang/nualang-api-and-queries/APIs/Bots";
import {
  getCourseSectionTopicProgress,
  getBotProgress,
  createProgress as createProgressAPI,
  deleteBotProgress as deleteBotProgressAPI,
  deleteCourseSectionTopicProgress as deleteCourseSectionTopicProgressAPI,
} from "@nualang/nualang-api-and-queries/APIs/Progress";
import {
  createCompletion as createCompletionAPI,
  getCourseSectionTopicCompletions,
  getBotCompletions,
} from "@nualang/nualang-api-and-queries/APIs/Completions";
import { getAssignment } from "@nualang/nualang-api-and-queries/APIs/Assignments";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import {
  highlightSpeechText,
  stopHighlightingSpeechText,
  searchStringToObj,
  objToSearchString,
  trackRecommendedEvent,
} from "../../../utils/index";
import { getUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import { getClassroom } from "@nualang/nualang-api-and-queries/APIs/Classrooms";
import { getClassroomMember } from "@nualang/nualang-api-and-queries/APIs/ClassroomMembers";
import { reportProblem as reportProblemAPI } from "@nualang/nualang-api-and-queries/APIs/Notifications";
import {
  courses as courseHelpers,
  bots as botHelpers,
  progress as progressHelpers,
  completions as completionHelpers,
  classrooms as classroomHelpers,
  notifications as notificationHelpers,
  users as userHelpers,
  sessionReports as sessionReportHelpers,
} from "../../../utils/helpers";
import {
  createBotSessionReport as createBotSessionReportAPI,
  getBotSessionReport as getBotSessionReportAPI,
} from "../../../utils/api/bots/api/SessionReports";
import * as sessionReportQuerys from "../../../utils/api/bots/hooks/sessionReports";
import { usesDynamicResponses } from "@nualang/nualang-ui-components/dist/utils/dynamicResponses";
import {
  UploadBotsAudio,
  UploadReportProblemAudio,
} from "../../../utils/audioUpload";
import { v4 as uuidv4 } from "uuid";
import { useQueryClient } from "@tanstack/react-query";
import { getForLangCharacters } from "../../../utils/index";
import { UploadReportProblemImage } from "../../../utils/imageUpload";

import logger from "../../../logger";
import { useApp } from "../../../hooks";

const emptyModeProgress = {
  existingMessages: null,
  existingVars: null,
  exerciseStartTime: null,
};

const formatBotProgress = (topicProgress = [], botId) => {
  const findForExercise = (exercise) => {
    const match = topicProgress.find(
      (p) => p.botId === botId && p.exercise === exercise,
    );
    if (!match) return emptyModeProgress;
    return {
      existingMessages: match.existingMessages,
      existingVars: match.existingVars,
      exerciseStartTime: match.exerciseStartTime,
    };
  };

  return {
    chat: findForExercise("bot"),
    audioOnly: findForExercise("bot-listen-and-speak"),
  };
};

const formatBotCompletion = (topicCompletions = [], botId) => {
  const countFor = (exercise) =>
    topicCompletions.filter((c) => c.botId === botId && c.exercise === exercise)
      .length;

  return {
    chat: countFor("bot"),
    audioOnly: countFor("bot-listen-and-speak"),
  };
};

function PlayBot(props) {
  const { t } = useTranslation();
  const [tts, translate] = useServices();
  const [timeoutArray, setTimeoutArray] = useState([]);

  const {
    authData: { username, attributes },
    navigate,
    handleSignIn,
    authenticated,
  } = props;

  const queryClient = useQueryClient();

  const audioPlayer = useAudioPlayer();
  const { addToQueue, toggleMuteSound, clearQueue, stop } = audioPlayer;

  const location = useLocation();
  const searchParams = searchStringToObj(location.search);
  const params = useParams();
  const { courseId, sectionId, topicId, botId, classroomId } = params;

  const searchAssignmentParams = new URLSearchParams(location.search);
  const assignmentId = searchAssignmentParams.get("assignmentId");
  const requestedBotMode = searchAssignmentParams.get("mode");

  const { sendExerciseCompletionEmail } = notificationHelpers;
  const {
    startLoading,
    stopLoading,
    openSnackbar,
    handleShareBot,
    markCommunityAccessRestricted,
  } = useApp();

  const reportProblem = async (body) => {
    try {
      startLoading();
      await reportProblemAPI(body);
      stopLoading();
      openSnackbar("problem_reported", "success");
    } catch (error) {
      logger.error(error);
      stopLoading();
      openSnackbar("problem", "error");
    }
  };

  const parentClassroomQuery = classroomQuerys.useClassroom(
    async () => {
      const classroom = await getClassroom(classroomId);
      const formattedClassroom = await classroomHelpers.formatClassroom(
        classroom.Item,
        false,
        props.authData,
      );
      return formattedClassroom;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const parentClassroom = useMemo(
    () =>
      parentClassroomQuery.isSuccess &&
      parentClassroomQuery.data &&
      parentClassroomQuery.data.classroomId
        ? parentClassroomQuery.data
        : {},
    [parentClassroomQuery.data, parentClassroomQuery.isSuccess],
  );

  // Fallback signal: this classroom may already be flagged by the backend as
  // community-access-restricted before the user's own record ever gets
  // flagged (see appAtoms.js). Learn about it as soon as we see it.
  useEffect(() => {
    markCommunityAccessRestricted([parentClassroom]);
  }, [parentClassroom, markCommunityAccessRestricted]);

  const { courseSettings } = parentClassroom;

  // The classroom's all-AI and chatbot switches, and the course's, can each
  // turn dynamic responses off whatever the bot says. Outside a classroom only
  // the bot decides. If the classroom can't be loaded, AI stays off.
  const isClassroomLoading =
    !!classroomId &&
    !parentClassroomQuery.isSuccess &&
    !parentClassroomQuery.isError;
  const isClassroomAIAllowed =
    !classroomId ||
    (parentClassroomQuery.isSuccess &&
      isCourseAIEnabled(parentClassroom, courseSettings?.[courseId]));

  const classroomMemberQuery = classroomQuerys.useClassroomMember(
    async () => {
      const member = await getClassroomMember(classroomId, username);
      return member;
    },
    {
      classroomId,
      username,
    },
    { enabled: !!classroomId },
  );

  const classroomMember =
    classroomMemberQuery.isSuccess &&
    classroomMemberQuery.data &&
    classroomMemberQuery.data.Item
      ? classroomMemberQuery.data.Item
      : {};

  const classroomAssignmentQuery = assignmentQuerys.useAssignment(
    async (classroomId, assignmentId) => {
      const assignment = await getAssignment(classroomId, assignmentId);
      return assignment.Item;
    },
    {
      classroomId,
      assignmentId,
    },
    { enabled: !!assignmentId },
  );

  const assignment =
    classroomAssignmentQuery.isSuccess && classroomAssignmentQuery.data
      ? classroomAssignmentQuery.data
      : [];

  const { uniqueAssignmentCoursesIds } = useMemo(() => {
    const courses = new Set();

    assignment?.exercises?.forEach(({ courseSectionTopicId }) => {
      if (courseSectionTopicId) {
        const [courseId] = courseSectionTopicId.split("|");
        courses.add(courseId);
      }
    });

    return {
      uniqueAssignmentCoursesIds: [...courses],
    };
  }, [assignment?.exercises]);

  const assignedBotMode = useMemo(() => {
    if (!assignmentId) return undefined;
    if (!classroomAssignmentQuery.isSuccess) return undefined;
    const assignedExercise = assignment?.exercises?.find(
      (exercise) => exercise.botId === botId,
    );
    return assignedExercise?.mode || "all";
  }, [assignmentId, classroomAssignmentQuery.isSuccess, assignment?.exercises, botId]);

  const botQuery = botQuerys.useBot(
    async () => {
      const bot = await getBot(botId);
      const formattedBot = await botHelpers.formatBot(bot.Item, username);
      return formattedBot;
    },
    {
      botId,
    },
    {
      botId,
    },
  );
  let bot = botQuery;

  const contentCreatorId = useMemo(
    () => parentClassroom?.createdBy ?? bot?.data?.createdBy,
    [parentClassroom?.createdBy, bot?.data?.createdBy],
  );

  const botCreatorId = useMemo(
    () => bot?.data?.createdBy,
    [bot?.data?.createdBy],
  );

  const contentCreatorQuery = userQuerys.useUser(
    async (userId) => {
      const response = await getUser(userId, true);
      return response;
    },
    {
      userId: contentCreatorId,
    },
    {
      enabled: !!contentCreatorId,
    },
  );

  const botCreatorQuery = userQuerys.useUser(
    async (userId) => {
      const response = await getUser(userId, true);
      return response;
    },
    {
      userId: botCreatorId,
    },
    {
      enabled: !!botCreatorId && botCreatorId !== contentCreatorId,
    },
  );

  const contentCreator =
    contentCreatorQuery.isSuccess && contentCreatorQuery.data
      ? contentCreatorQuery.data
      : {};

  const botCreator = useMemo(() => {
    if (botCreatorId === contentCreatorId) {
      return contentCreator;
    }
    return botCreatorQuery.isSuccess && botCreatorQuery.data
      ? botCreatorQuery.data
      : null;
  }, [
    botCreatorId,
    contentCreatorId,
    contentCreator,
    botCreatorQuery.isSuccess,
    botCreatorQuery.data,
  ]);

  // Mutations
  const createProgressMutation = progressQuerys.useCreateProgress(
    createProgressAPI,
    {
      onSuccess: (data, variables) => {
        progressHelpers.invalidateProgressCache({
          courseId,
          sectionId,
          topicId,
          exercise: variables.values.exercise,
          botId,
          memberId: username,
          createdAt: data.Item.createdAt,
        });
        queryClient.invalidateQueries({
          queryKey: ["bots", "inProgress", username],
        });
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );
  const deleteTopicProgressMutation = progressQuerys.useDeleteTopicProgress(
    deleteCourseSectionTopicProgressAPI,
    {
      onSuccess: async (data, variables) => {
        await progressHelpers.invalidateProgressCache({
          courseId: variables.courseId,
          sectionId: variables.sectionId,
          topicId: variables.topicId,
          exercise: variables.exercise,
          memberId: variables.memberId,
        });
        if (variables.courseId && variables.sectionId && variables.topicId) {
          queryClient.invalidateQueries({
            queryKey: progressQuerys.progressKeys.listTopicMember(
              variables.courseId,
              variables.sectionId,
              variables.topicId,
              variables.memberId,
              null,
            ),
          });
        }
      },
      onError: () => {
        // allow to fail without showing snackbar
        // openSnackbar('problem', 'error');
      },
    },
  );
  const deleteBotProgressMutation = progressQuerys.useDeleteBotProgress(
    deleteBotProgressAPI,
    {
      // useDeleteBotProgress calls this as (data, variables) - the mutation's own
      // mutationFn args, not `data`, carry botId/exercise/memberId.
      onSuccess: (data, variables) => {
        progressHelpers.invalidateProgressCache({
          courseId,
          sectionId,
          topicId,
          botId: variables.botId,
          exercise: variables.exercise,
          memberId: variables.memberId,
        });
      },
      onError: () => {
        // allow to fail without showing snackbar
        // openSnackbar('problem', 'error');
      },
    },
  );
  // Reports are only generated for sessions that used dynamic (AI) responses, resolved from
  // the same bot, classroom and course settings the Bot exercise uses for the message API.
  const sessionUsesDynamicResponses = usesDynamicResponses({
    botSetting: bot?.data?.isDynamicResponsesEnabled,
    parentClassroom,
    courseSettings: courseSettings?.[courseId],
    selectedTemplate: bot?.data?.selectedTemplate,
  });

  // End-of-session report: requested once the completion is saved, then polled until it
  // settles. The completion dialog shows its areas of strength and growth.
  // isAwaitingCompletion covers the gap between submitting the completion and it being saved,
  // so the dialog shows "preparing" straight away instead of the previous attempt's report.
  const [isAwaitingCompletion, setIsAwaitingCompletion] = useState(false);
  const [sessionReportRequest, setSessionReportRequest] = useState(null);
  const [sessionReportRequestFailed, setSessionReportRequestFailed] =
    useState(false);
  const [sessionReportTimedOut, setSessionReportTimedOut] = useState(false);

  const createSessionReportMutation =
    sessionReportQuerys.useCreateBotSessionReport(createBotSessionReportAPI, {
      onError: (error) => {
        if (!sessionReportHelpers.isConflictError(error)) {
          setSessionReportRequestFailed(true);
        }
      },
    });

  const resetSessionReport = () => {
    setSessionReportRequest(null);
    setSessionReportRequestFailed(false);
    setSessionReportTimedOut(false);
    createSessionReportMutation.reset();
  };

  const requestSessionReport = ({ createdAt, messages }) => {
    if (!createdAt || !messages.length) return;
    setSessionReportRequest({ botId, memberId: username, createdAt });
    setSessionReportRequestFailed(false);
    setSessionReportTimedOut(false);
    createSessionReportMutation.mutate({
      botId,
      body: {
        completionCreatedAt: createdAt,
        i18nLanguage: i18n.language,
        messages,
      },
    });
  };

  useEffect(() => {
    if (!sessionReportRequest) return undefined;
    const timer = setTimeout(
      () => setSessionReportTimedOut(true),
      sessionReportHelpers.SESSION_REPORT_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [sessionReportRequest]);

  const isSessionReportRequested =
    createSessionReportMutation.isSuccess ||
    sessionReportHelpers.isConflictError(createSessionReportMutation.error);

  const sessionReportQuery = sessionReportQuerys.useBotSessionReport(
    getBotSessionReportAPI,
    sessionReportRequest || {},
    {
      enabled:
        !!sessionReportRequest &&
        isSessionReportRequested &&
        !sessionReportTimedOut,
      refetchInterval: (query) =>
        sessionReportHelpers.isSessionReportSettled(query.state.data)
          ? false
          : sessionReportHelpers.SESSION_REPORT_POLL_MS,
    },
  );

  const sessionReport = sessionReportHelpers.toSessionReportSummary({
    requested: isAwaitingCompletion || !!sessionReportRequest,
    requestFailed: sessionReportRequestFailed,
    timedOut: sessionReportTimedOut,
    response: sessionReportQuery.data,
  });

  const sessionReportAttemptPath = sessionReportRequest
    ? sessionReportHelpers.getBotAttemptPath({
        classroomId,
        courseId,
        sectionId,
        topicId,
        memberId: sessionReportRequest.memberId,
        createdAt: sessionReportRequest.createdAt,
      })
    : null;

  const createCompletionMutation = completionQuerys.useCreateTopicCompletion(
    createCompletionAPI,
    {
      onSuccess: async (data) => {
        queryClient.invalidateQueries({
          queryKey: ["bots", "completed", username],
        });
        sendCompletionEmail();
        const { Item = {} } = data;
        const { messages = [], createdAt = "", botName = "chatbot" } = Item;
        setIsAwaitingCompletion(false);
        if (sessionUsesDynamicResponses) {
          requestSessionReport({ createdAt, messages });
        }
        const containsStudentInappropriate = messages.some(
          (msg) => msg?.studentInappropriate === true,
        );
        if (containsStudentInappropriate) {
          const responseFromMisuse = await handleStudentMisuse({
            params: {
              exerciseName: botName,
            },
            botId: bot.botId,
            createdAt,
            courseId,
            sectionId,
            topicId,
          });
          if (responseFromMisuse && responseFromMisuse.error) {
            openSnackbar(responseFromMisuse.error, "error");
          } else {
            openSnackbar(t("student_misuse_reported"), "info");
          }
        }
      },
      onError: () => {
        setIsAwaitingCompletion(false);
        openSnackbar("problem", "error");
      },
    },
  );

  const [isBotOpen, setIsBotOpen] = useState(true);
  const handleCloseBot = () => {
    setIsBotOpen(false);
    if (courseId && sectionId && topicId) {
      if (classroomId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}`,
        );
      } else {
        navigate(`/courses/${courseId}/${sectionId}/${topicId}`);
      }
    } else {
      navigate("/bots");
    }
  };

  const userQuery = userQuerys.useUser(
    userHelpers.getOwnUser,
    {
      userId: username,
    },
    {
      enabled: !!username,
    },
  );

  const userAttributes =
    userQuery.isSuccess && userQuery.data && userQuery.data.UserAttributes
      ? userQuery.data.UserAttributes
      : {};

  const courseSectionTopicProgressQuery = progressQuerys.useTopicMemberProgress(
    async (courseId, sectionId, topicId, memberId, returnvalues) => {
      const memberProgress = await getCourseSectionTopicProgress(
        courseId,
        sectionId,
        topicId,
        memberId,
        returnvalues,
      );
      const formattedProgress = await progressHelpers.formatProgress(
        memberProgress.Items,
        username,
        attributes.preferred_username,
      );
      return formattedProgress;
    },
    {
      courseId,
      sectionId,
      topicId,
      memberId: username,
      returnvalues: null,
    },
    {
      enabled:
        !!courseId && !!sectionId && !!topicId && !!username && !!attributes,
    },
  );

  const botProgressQuery = progressQuerys.useBotMemberProgress(
    async (botId, memberId, filters) => {
      const response = await getBotProgress(botId, memberId, filters);
      const formattedProgress = await progressHelpers.formatProgress(
        response.Items,
        memberId,
        attributes.preferred_username,
      );
      return formattedProgress;
    },
    {
      botId,
      memberId: username,
      filters: null,
    },
    {
      enabled: !!botId && !!username && !courseId,
    },
  );

  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  // Reset the latch whenever the target bot/topic changes - PlayBot is reused
  // across route param changes rather than remounted, so a latch left over
  // from a PREVIOUS bot/topic must not let BotExercise render with this new
  // target's progress query still pending (stale or empty progress).
  useEffect(() => {
    setHasLoadedProgress(false);
  }, [courseId, sectionId, topicId, botId]);
  useEffect(() => {
    if (courseSectionTopicProgressQuery.isSuccess || botProgressQuery.isSuccess) {
      setHasLoadedProgress(true);
    }
  }, [courseSectionTopicProgressQuery.isSuccess, botProgressQuery.isSuccess]);

  const botExerciseProgress = useMemo(
    () =>
      formatBotProgress(
        courseId ? courseSectionTopicProgressQuery.data : botProgressQuery.data,
        bot && bot.data ? bot.data.botId : null,
      ),
    [
      bot,
      botProgressQuery.data,
      courseId,
      courseSectionTopicProgressQuery.data,
    ],
  );

  const courseSectionTopicCompletionsQuery =
    completionQuerys.useTopicMemberCompletions(
      async (cId, sId, tId, memberId, returnvalues) => {
        const memberCompletions = await getCourseSectionTopicCompletions(
          cId,
          sId,
          tId,
          memberId,
          returnvalues,
        );
        const formattedCompletions = await completionHelpers.formatCompletions(
          memberCompletions.Items,
        );
        return formattedCompletions;
      },
      {
        courseId,
        sectionId,
        topicId,
        memberId: username,
        returnvalues: null,
      },
      {
        enabled:
          !!courseId && !!sectionId && !!topicId && !!username && !!attributes,
      },
    );

  const botCompletionsQuery = completionQuerys.useBotCompletions(
    async (bId) => {
      const response = await getBotCompletions(bId, username);
      const formattedCompletions = await completionHelpers.formatCompletions(
        response.Items,
      );
      return formattedCompletions;
    },
    {
      botId,
      filters: null,
    },
    {
      enabled: !!botId && !!username && !courseId,
    },
  );

  const botExerciseCompletion = useMemo(
    () =>
      formatBotCompletion(
        courseId
          ? courseSectionTopicCompletionsQuery.data
          : botCompletionsQuery.data,
        bot && bot.data ? bot.data.botId : null,
      ),
    [
      bot,
      botCompletionsQuery.data,
      courseId,
      courseSectionTopicCompletionsQuery.data,
    ],
  );

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );
  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};

  const { forLang = "" } = course;

  const handleSpeak = async (params) => {
    try {
      const {
        text,
        learnLanguage,
        voiceName,
        mute,
        callback,
        textContainer,
        languageCode,
        voiceSpeed,
        pitch,
        onSpeechStart,
      } = params;
      const result = await tts(
        text.replace(/\\/g, ''), // Remove backslashes from the text before sending to TTS
        learnLanguage,
        voiceName,
        languageCode,
        voiceSpeed,
        pitch,
      );
      if (!mute && result && result.audioUrl) {
        const highlightCallback = () =>
          highlightSpeechText(
            textContainer,
            result.speechMarks,
            setTimeoutArray,
          );
        addToQueue(
          result.audioUrl,
          callback,
          highlightCallback,
          false,
          onSpeechStart,
        );
      } else if (result.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const handleStopSpeaking = async (textContainer) => {
    try {
      stopHighlightingSpeechText(textContainer, timeoutArray);
      stop();
      clearQueue();
    } catch (error) {
      logger.error(error);
    }
  };

  const addSearchParams = (params) => {
    navigate({
      ...location,
      search: objToSearchString({
        ...searchParams,
        ...params,
      }),
    });
  };

  const removeSearchParams = (keys = []) => {
    for (const key of keys) {
      key in searchParams && delete searchParams[key];
    }
    navigate({
      ...location,
      search: objToSearchString(searchParams),
    });
  };

  const handlePlayBot = async (bot) => {
    const { courseSectionTopicId, botId } = bot;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      if (classroomId) {
        navigate(
          `/classroom/${classroomId}/${courseId}/${sectionId}/${topicId}/bots/${botId}`,
        );
      } else {
        navigate(`/courses/${courseId}/${sectionId}/${topicId}/bots/${botId}`);
      }
    } else {
      navigate(`/bots/${botId}`);
    }
  };

  const handleEditBot = async (bot) => {
    const { courseSectionTopicId, botId } = bot;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      if (classroomId) {
        navigate(
          `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/bots/${botId}/edit`,
        );
      } else {
        navigate(
          `/courses/${courseId}/${sectionId}/${topicId}/bots/${botId}/edit`,
        );
      }
    } else {
      navigate(`/bots/${botId}/edit`);
    }
  };

  const handleStudentMisuse = async (body) => {
    try {
      const { params, createdAt } = body;
      startLoading();
      const exerciseLink = `${window.location.origin}/courses/${courseId}/${sectionId}/${topicId}/activity/bot/${username}/${createdAt}`;
      const formattedParams = {
        ...params,
        exerciseLink,
        creatername: contentCreator?.UserAttributes?.preferred_username,
        studentName: attributes ? attributes.preferred_username : "",
      };
      const isCreatorReceivingReportedProblem = [true, undefined].includes(
        contentCreator?.UserAttributes?.email_notification
          ?.student_acts_inappropriately,
      );
      if (!isCreatorReceivingReportedProblem) {
        return;
      }

      const teacherUsername =
        parentClassroom && parentClassroom.createdBy
          ? parentClassroom?.createdBy
          : process.env.REACT_APP_NUALANG_SUPPORT_EMAIL;
      const recipients = [teacherUsername];
      await studentMisuseAPI({
        params: formattedParams,
        recipients,
      });
      stopLoading();
    } catch (error) {
      logger.error(error);
      stopLoading();
    }
  };

  const handleReportProblem = async (
    problem,
    shareAudio,
    audioBlob,
    shareScreenshot,
    screenshot,
  ) => {
    try {
      const { type, description, currentPhrase } = problem;
      const isCreatorReceivingReportedProblem = [true, undefined].includes(
        botCreator?.UserAttributes?.email_notification?.student_reports_problem,
      );

      let recipients;
      if (type === "equipment_problem") {
        const teacherUsername =
          parentClassroom && parentClassroom.createdBy
            ? parentClassroom?.createdBy
            : process.env.REACT_APP_NUALANG_SUPPORT_EMAIL;
        recipients = [teacherUsername];
      } else {
        recipients =
          isCreatorReceivingReportedProblem && botCreator && type === "content_problem"
            ? [bot?.data?.createdBy, config.supportEmail]
            : [config.supportEmail];
      }

      const problemType = (() => {
        switch (type) {
          case "equipment_problem":
            return "Equipment Problem";
          case "technical_problem":
            return "Technical Problem";
          case "content_problem":
            return "Content Problem";
          default:
            return "Other Problem";
        }
      })();

      const [audioUrl, screenshotUrl] = await Promise.all([
        shareAudio && audioBlob
          ? UploadReportProblemAudio(audioBlob, username)
          : Promise.resolve(null),
        shareScreenshot && screenshot
          ? UploadReportProblemImage(screenshot, username)
          : Promise.resolve(null),
      ]);

      const body = {
        recipients,
        params: {
          exerciseLink: `${window.location.origin}${window.location.pathname}`,
          exerciseName: `${bot?.data?.botName} - chatbot`,
          studentName: attributes ? attributes.preferred_username : "",
          problemDescription: description,
          problemType: problemType,
          email: attributes ? attributes.email : "",
          username: attributes ? attributes["preferred_username"] : "",
          role: attributes ? attributes["custom:role"] : "",
          browser: JSON.stringify(
            parseUserAgent(window.navigator.userAgent).browser,
          ),
          os: JSON.stringify(parseUserAgent(window.navigator.userAgent).os),
          isWaysideUser: username?.startsWith("Wayside"),
          phrase: currentPhrase,
          ...(audioUrl && { audioUrl }),
          ...(screenshotUrl && { screenshotUrl }),
        },
      };
      await reportProblem(body);
    } catch (error) {
      logger.error(error);
    }
  };

  const sendCompletionEmail = async () => {
    try {
      if (bot?.data?.createdBy) {
        if (
          botCreator?.UserAttributes &&
          botCreator.UserAttributes.email_notification &&
          botCreator.UserAttributes.email_notification
            .student_completes_exercise === true &&
          username !== bot?.data?.createdBy
        ) {
          let url;
          const studentName = attributes ? attributes.preferred_username : "";
          const { courseSectionTopicId, botId } = bot?.data;
          const idValues = courseSectionTopicId
            ? courseSectionTopicId.split("|")
            : [];
          if (idValues && idValues.length === 3) {
            const courseId = idValues[0].trim();
            const sectionId = idValues[1].trim();
            const topicId = idValues[2].trim();
            url = `${window.location.origin}/courses/${courseId}/${sectionId}/${topicId}/activity/bot?botId=${botId}`;
          } else {
            url = `${window.location.href}/activity`;
          }
          sendExerciseCompletionEmail(
            [bot?.data?.createdBy],
            studentName,
            botCreator.UserAttributes.preferred_username,
            bot?.data?.botName,
            "Chatbot",
            url,
          );
        }
      }
    } catch (error) {
      logger.error(error);
    }
  };

  const handleSaveProgress = (values) => {
    createProgressMutation.mutate({ values });
  };

  const handleCreateCompletion = (values) => {
    const classroomCourseIds =
      parentClassroom?.courses?.length > 0
        ? parentClassroom.courses.toString()
        : null;
    const classroomAssignedCourseIds =
      parentClassroom?.assignedCourses?.length > 0
        ? parentClassroom.assignedCourses.toString()
        : null;
    resetSessionReport();
    setIsAwaitingCompletion(sessionUsesDynamicResponses);
    createCompletionMutation.mutate({
      values,
      botId,
      classroomCourseIds,
      classroomAssignedCourseIds,
      uniqueAssignmentCoursesIds,
    });
  };

  // useExerciseState (ui-components) passes the exercise's ids as one object, as in
  // ViewTopic; a bare id is still accepted. A wrong botId here deleted nothing, so a
  // completed chatbot kept its progress and completed again when reopened.
  const handleDeleteTopicProgress = async (
    courseId,
    sectionId,
    topicId,
    exercise,
    ids,
  ) => {
    deleteTopicProgressMutation.mutate({
      courseId,
      sectionId,
      topicId,
      exercise,
      memberId: username,
      botId: typeof ids === "object" && ids !== null ? ids.botId : ids,
    });
  };

  const handleDeleteBotProgress = async (botId, exercise) => {
    deleteBotProgressMutation.mutate({ botId, exercise, memberId: username });
  };

  const uploadAudioAttempt = async (audioBlob) => {
    const url = await UploadBotsAudio(audioBlob, `${botId}_${uuidv4()}`);
    return url;
  };

  const learnLang = bot?.isSuccess ? (bot.data.learnLang ?? {}) : {};
  const learnLangCharacters =
    learnLang && Object.keys(learnLang).length !== 0
      ? config.languages[learnLang.toLowerCase()].characters
      : [];
  const forLangCharacters = getForLangCharacters(forLang);

  const formatAiChatbotConversationHistory = (existingMessages) =>
    (Array.isArray(existingMessages) ? existingMessages : [])
      .filter(
        (item) =>
          item &&
          typeof item.isMyMessage !== "undefined" &&
          typeof item.text === "string",
      )
      .map((item) => {
        return item.isMyMessage
          ? { role: "user", content: item.text.trim() }
          : { role: "assistant", content: item.text.trim() };
      });

  const aiChatbotConversationHistory = formatAiChatbotConversationHistory(
    botExerciseProgress.chat.existingMessages,
  );
  const audioOnlyAiChatbotConversationHistory =
    formatAiChatbotConversationHistory(
      botExerciseProgress.audioOnly.existingMessages,
    );

  const isUserInternal = config.internalDomains.some((domain) =>
    (userAttributes.email || "").endsWith(domain),
  );

  return (
    <div>
      {bot &&
      bot.data &&
      bot.data.botId &&
      !isClassroomLoading &&
      (authenticated
        ? courseSectionTopicProgressQuery.isSuccess ||
          botProgressQuery.isSuccess ||
          hasLoadedProgress
        : true) &&
      (!assignmentId || classroomAssignmentQuery.isSuccess) ? (
        <BotExercise
          isDynamicResponsesEnabled={
            !isClassroomAIAllowed
              ? "disable"
              : bot && bot.isSuccess && bot.data
                ? bot.data.isDynamicResponsesEnabled
                : null
          }
          aiChatbotConversationHistory={aiChatbotConversationHistory}
          i18nLanguage={i18n.language}
          t={t}
          bot={bot && bot.isSuccess && bot.data ? bot.data : {}}
          handleClose={handleCloseBot}
          open={isBotOpen}
          learnLangCharacters={learnLangCharacters}
          authenticated={authenticated}
          placeholder={t("bot_input_placeholder")}
          handleSignIn={handleSignIn}
          forLangCharacters={forLangCharacters}
          isBotHidden={bot ? bot.isBotHidden : null}
          handleSaveProgress={handleSaveProgress}
          handleDeleteProgress={handleDeleteTopicProgress}
          handleDeleteBotProgress={handleDeleteBotProgress}
          courseId={courseId}
          sectionId={sectionId}
          topicId={topicId}
          handleStopSpeaking={handleStopSpeaking}
          navigate={navigate}
          handleSpeak={handleSpeak}
          learnLang={learnLang}
          forLang={forLang}
          openSnackbar={openSnackbar}
          exerciseName="bot"
          handleTranslate={translate}
          handleComplete={handleCreateCompletion}
          handleMessageBot={messageBot}
          userAttributes={userAttributes || {}}
          addSearchParams={addSearchParams}
          removeSearchParams={removeSearchParams}
          handlePlayBot={handlePlayBot}
          handleEditBot={handleEditBot}
          handleShareBot={handleShareBot}
          handleReportProblem={handleReportProblem}
          toggleMuteSound={toggleMuteSound}
          uploadAudioAttempt={uploadAudioAttempt}
          courseSettings={courseSettings?.[courseId]}
          voices={
            bot && bot.data.learnLang
              ? config.languages[bot.data.learnLang.toLowerCase() || "english"]
                  .Voices
              : null
          }
          existingMessages={botExerciseProgress.chat.existingMessages}
          existingVars={botExerciseProgress.chat.existingVars}
          exerciseStartTime={botExerciseProgress.chat.exerciseStartTime}
          audioOnlyExistingMessages={botExerciseProgress.audioOnly.existingMessages}
          audioOnlyExistingVars={botExerciseProgress.audioOnly.existingVars}
          audioOnlyExerciseStartTime={botExerciseProgress.audioOnly.exerciseStartTime}
          audioOnlyAiChatbotConversationHistory={audioOnlyAiChatbotConversationHistory}
          chatCompletedCount={botExerciseCompletion.chat}
          audioOnlyCompletedCount={botExerciseCompletion.audioOnly}
          clearQueue={clearQueue}
          stopAudio={stop}
          parentClassroom={parentClassroom}
          isReadWriteModeStudent={
            classroomMember?.assignedLabels?.isReadWriteModeStudent
          }
          classroomId={classroomId}
          assignment={assignmentId}
          assignedBotMode={assignedBotMode}
          requestedBotMode={requestedBotMode}
          isUserInternal={isUserInternal}
          username={username}
          trackRecommendedEvent={trackRecommendedEvent}
          sessionReport={sessionReport}
          viewSessionReport={
            sessionReportAttemptPath
              ? () => navigate(sessionReportAttemptPath)
              : null
          }
        />
      ) : (
        <></>
      )}
    </div>
  );
}

PlayBot.propTypes = {
  navigate: PropTypes.func,
  authData: PropTypes.object,
};

export default PlayBot;
